import { ReactElement } from 'react';

import { DocumentAttachment, FormGroup, ImageAttachment, VideoAttachment } from 'components';
import { FILE_NAME_LENGTH_LIMIT } from 'constants/index';
import { useWindowSize } from 'hooks';
import { Attachment } from 'types';
import { hideDocNameMidPart } from 'utils';

import {
  imagesFormGroupStyles,
  imagesFormGroupMobileStyles,
  filesFormGroupStyles,
  filesFormGroupMobileStyles,
} from './styles';

interface FormAttachmentsProps {
  attachedImages: Attachment[];
  attachedDocs: Attachment[];
  attachedVideos: Attachment[];
  handleDeleteAttachment: (attachmentId: string, type: 'images' | 'docs' | 'videos') => void;
  draftId?: string;
  hasDownloadDoc?: boolean;
  blockDelete?: boolean;
}

export function FormAttachments({
  attachedImages,
  attachedDocs,
  attachedVideos,
  draftId,
  handleDeleteAttachment,
  hasDownloadDoc,
  blockDelete,
}: FormAttachmentsProps): ReactElement {
  const { isMobile } = useWindowSize();
  return (
    <>
      {attachedImages.length > 0 && (
        <FormGroup row sx={isMobile ? imagesFormGroupMobileStyles : imagesFormGroupStyles}>
          {attachedImages.map(({ id, file, file_url }) => (
            <ImageAttachment
              key={id}
              postId={draftId}
              attachmentId={id}
              fileName={file?.small || 'small.png'}
              fileUrl={file_url?.small}
              largeFileUrl={file_url?.large}
              handleDelete={() => handleDeleteAttachment(id, 'images')}
              blockDelete={blockDelete}
            />
          ))}
        </FormGroup>
      )}
      {attachedVideos.length > 0 && (
        <FormGroup row sx={isMobile ? imagesFormGroupMobileStyles : imagesFormGroupStyles}>
          {attachedVideos.map(
            ({ id, file_url }: { id: string; file_url: { video_uid: string } }) => (
              <VideoAttachment
                key={id}
                postId={draftId}
                attachmentId={file_url?.video_uid}
                handleDelete={() => handleDeleteAttachment(id, 'videos')}
                wrapperSx={{
                  width: '80px',
                  height: '80px',
                  marginRight: '0px',
                  overflow: 'visible',
                }}
              />
            ),
          )}
        </FormGroup>
      )}
      {attachedDocs.length > 0 && (
        <FormGroup sx={isMobile ? filesFormGroupMobileStyles : filesFormGroupStyles}>
          {attachedDocs.map(({ id, file_url, file, display_name, file_extension }) => {
            const tempName = file?.original || display_name;
            const fileName =
              tempName?.length > FILE_NAME_LENGTH_LIMIT
                ? hideDocNameMidPart({
                    docName: tempName,
                    limit: FILE_NAME_LENGTH_LIMIT,
                  })
                : tempName;

            return (
              <DocumentAttachment
                key={id}
                postId={draftId}
                attachmentId={id}
                fileName={fileName}
                file_extension={file_extension}
                handleDelete={() => handleDeleteAttachment(id, 'docs')}
                hasDownload={hasDownloadDoc}
                originalFileName={tempName}
                originalFileUrl={file_url?.original}
                blockDelete={blockDelete}
              />
            );
          })}
        </FormGroup>
      )}
    </>
  );
}
